<template>
  <generic-modal
    @close="$emit('close')"
    title="Nieuwe cases (batch)"
    :show-actions="false"
    width="'700px'"
  >
    <template v-if="step === 'form'">
      <form-field
        label="Adres toevoegen"
        placeholder="Zoek naar een adres"
        :options="addresses"
        type="multi"
        remote-search
        v-model="pickedLocationId"
        @search-change="search"
        class="tw-w-[350px]"
      />

      <div class="tw-mt-2">
        <label class="tw-inline-block tw-uppercase tw-tracking-wide tw-text-gray-700 tw-text-xs tw-font-bold tw-mb-2">
          Of plak een lijst (één adres of postcode + huisnummer per regel)
        </label>
        <textarea
          v-model="pasteText"
          rows="3"
          placeholder="1234AB 12&#10;1234AB 14"
          class="tw-w-full tw-bg-gray-200 tw-rounded tw-text-md tw-text-gray-500 tw-p-2 tw-focus:bg-white tw-border tw-appearance-none tw-focus:outline-none"
        />
        <button
          class="tw-bg-gray-600 hover:tw-bg-gray-500 tw-rounded tw-p-1 tw-px-2 tw-text-white disabled:tw-opacity-40"
          :disabled="!pasteText.trim() || resolvingPaste"
          @click="addPastedAddresses"
        >
          {{ resolvingPaste ? 'Adressen opzoeken...' : 'Lijst toevoegen' }}
        </button>
      </div>

      <div v-if="unresolvedLines.length > 0" class="tw-text-red-500 tw-mt-2 tw-text-sm">
        {{ unresolvedLines.length }} regels niet herkend, ze staan nog in het invoerveld
      </div>

      <div class="tw-mt-3">
        <div class="tw-text-sm tw-font-bold tw-text-gray-500">
          {{ selected.length }} adressen geselecteerd
          <span v-if="selected.length > MAX_BATCH_SIZE" class="tw-text-red-500">
            (maximaal {{ MAX_BATCH_SIZE }} per batch)
          </span>
        </div>
        <div class="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1 tw-max-h-40 tw-overflow-y-auto">
          <div
            v-for="address in selected"
            :key="address.location_id"
            class="tw-bg-gray-200 tw-text-gray-600 tw-text-xs tw-rounded tw-py-0.5 tw-px-2 tw-flex tw-items-center tw-gap-1"
          >
            {{ address.label }}
            <button class="tw-text-gray-400 hover:tw-text-gray-700" @click="removeAddress(address)">
              &#x2715;
            </button>
          </div>
        </div>
      </div>

      <form-field
        label="Categorie"
        :options="categories"
        type="select"
        v-model="form.category"
        :nullable="false"
        class="tw-mt-3"
      />
      <form-field
        type="textarea"
        label="Case omschrijving (voor alle adressen)"
        rows="3"
        v-model="form.description"
      />

      <div class="tw-flex tw-justify-end tw-mt-4">
        <button class="tw-bg-gray-500 tw-text-white tw-py-2 tw-px-4 tw-rounded tw-mr-2" @click="$emit('close')">
          Annuleren
        </button>
        <button
          class="tw-bg-green-600 tw-text-white tw-py-2 tw-px-4 tw-rounded disabled:tw-opacity-40"
          :disabled="!valid || batchLoading"
          @click="runDryRun"
        >
          Controleren
        </button>
      </div>
    </template>

    <template v-else>
      <div class="tw-text-sm tw-mb-2" v-if="step === 'preview'">
        Controleer de adressen. Er wordt nog niets aangemaakt.
      </div>
      <div class="tw-max-h-80 tw-overflow-y-auto tw-border tw-border-gray-200 tw-rounded">
        <div
          v-for="row in batchResult.data"
          :key="row.location_id"
          class="tw-flex tw-justify-between tw-gap-2 tw-px-2 tw-py-1 tw-border-b tw-border-gray-100 tw-text-sm"
        >
          <div>{{ row.address || row.location_id }}</div>
          <div v-if="row.result === 'can_create'" class="tw-text-green-600">Wordt aangemaakt</div>
          <router-link
            v-else-if="row.result === 'created'"
            :to="{ name: 'case-detail', params: { id: row.case_id } }"
            class="tw-text-green-600 hover:tw-underline"
            @click.native="$emit('close')"
          >
            Case #{{ row.case_id }} aangemaakt
          </router-link>
          <router-link
            v-else-if="row.result === 'skipped_existing_case'"
            :to="{ name: 'case-detail', params: { id: row.existing_case_id } }"
            class="tw-text-orange-500 hover:tw-underline"
            @click.native="$emit('close')"
          >
            Bestaande case #{{ row.existing_case_id }}, overgeslagen
          </router-link>
          <div v-else-if="row.result === 'unknown_location'" class="tw-text-red-500">
            Adres onbekend
          </div>
          <div v-else class="tw-text-red-500">{{ row.result }}</div>
        </div>
      </div>
      <div class="tw-text-sm tw-font-bold tw-text-gray-500 tw-mt-2">
        {{ summaryText }}
      </div>

      <div class="tw-flex tw-justify-end tw-mt-4">
        <template v-if="step === 'preview'">
          <button class="tw-bg-gray-500 tw-text-white tw-py-2 tw-px-4 tw-rounded tw-mr-2" @click="step = 'form'">
            Terug
          </button>
          <button
            class="tw-bg-green-600 tw-text-white tw-py-2 tw-px-4 tw-rounded disabled:tw-opacity-40"
            :disabled="creatableCount === 0 || batchLoading"
            @click="runCreate"
          >
            {{ batchLoading ? 'Bezig...' : `Maak ${creatableCount} cases aan` }}
          </button>
        </template>
        <template v-else>
          <button
            class="tw-bg-gray-500 tw-text-white tw-py-2 tw-px-4 tw-rounded tw-mr-2"
            @click="$emit('close')"
          >
            Sluiten
          </button>
          <button
            v-if="createdCaseIds.length > 0"
            class="tw-bg-green-600 tw-text-white tw-py-2 tw-px-4 tw-rounded"
            @click="$emit('createTickets', { caseIds: createdCaseIds, title: form.description })"
          >
            Maak tickets voor deze cases
          </button>
        </template>
      </div>
    </template>
  </generic-modal>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue'
import { mapWithConcurrency } from '@/util'
import { CASE_CATEGORIES, MAX_BATCH_SIZE, MAX_CONCURRENT_BULK_ACTIONS } from '@/constants'
import { searchLocations, useAddressOptions } from '@/util/locations'
import useBatchSubmit from '@/util/useBatchSubmit'
import FormField from '@/components/shared/FormField.vue'
import GenericModal from '@/components/shared/GenericModal.vue'

defineEmits(['close', 'createTickets'])

const categories = CASE_CATEGORIES

const form = reactive({
  description: '',
  category: categories[0],
})

const selected = ref([])
const pasteText = ref('')
const unresolvedLines = ref([])
const resolvingPaste = ref(false)

const addAddress = (address) => {
  if (!selected.value.some((a) => a.location_id === address.location_id)) {
    selected.value = [...selected.value, address]
  }
}

const removeAddress = (address) => {
  selected.value = selected.value.filter((a) => a.location_id !== address.location_id)
}

const pickedLocationId = ref(null)
const { addresses, search } = useAddressOptions()

watch(pickedLocationId, (locationId) => {
  if (!locationId) return
  const option = (addresses.value || []).find((o) => o.value === locationId)
  addAddress({ location_id: locationId, label: option?.label || locationId })
  pickedLocationId.value = null
})

const normalizeLine = (value) => value.replace(/[\s,;.]/g, '').toUpperCase()

// exact match only: a single similar search hit must not add a different address
const resolveLine = async (line) => {
  const candidate = normalizeLine(line)
  const findExact = (locations) =>
    locations.find(
      (l) => l.location_id === candidate || normalizeLine(l.legacy_address || '') === candidate
    )
  try {
    let match = findExact(await searchLocations(candidate))
    if (!match && candidate !== line) {
      match = findExact(await searchLocations(line))
    }
    return { line, match }
  } catch (e) {
    return { line, match: null }
  }
}

const addPastedAddresses = async () => {
  const lines = [...new Set(pasteText.value.split(/\r?\n/).map((l) => l.trim()).filter(Boolean))]

  resolvingPaste.value = true
  try {
    const resolved = await mapWithConcurrency(lines, MAX_CONCURRENT_BULK_ACTIONS, resolveLine)

    unresolvedLines.value = resolved.filter((r) => !r.match).map((r) => r.line)
    pasteText.value = unresolvedLines.value.join('\n')

    const merged = [...selected.value]
    const known = new Set(merged.map((address) => address.location_id))
    for (const { match } of resolved) {
      if (match && !known.has(match.location_id)) {
        known.add(match.location_id)
        merged.push({ location_id: match.location_id, label: match.legacy_address })
      }
    }
    selected.value = merged
  } finally {
    resolvingPaste.value = false
  }
}

const valid = computed(
  () =>
    selected.value.length > 0 &&
    selected.value.length <= MAX_BATCH_SIZE &&
    !!form.description &&
    !!form.category
)

const { step, batchResult, batchLoading, runDryRun, runCreate } = useBatchSubmit({
  url: '/tickets/api/v1/cases/batch',
  buildBody: () => ({
    location_ids: selected.value.map((a) => a.location_id),
    description: form.description,
    category: form.category,
  }),
  invalidateKeys: [['cases']],
  createdMessage: (created) => `${created} cases aangemaakt`,
})

const creatableCount = computed(() => batchResult.value?.summary?.can_create || 0)

const createdCaseIds = computed(
  () =>
    batchResult.value?.data
      ?.filter((row) => row.result === 'created')
      .map((row) => row.case_id) || []
)

const summaryText = computed(() => {
  const summary = batchResult.value?.summary || {}
  const parts = []
  if (step.value === 'preview') {
    parts.push(`${summary.can_create || 0} aan te maken`)
  } else {
    parts.push(`${summary.created || 0} aangemaakt`)
  }
  if (summary.skipped_existing_case) parts.push(`${summary.skipped_existing_case} overgeslagen`)
  if (summary.unknown_location) parts.push(`${summary.unknown_location} onbekend`)
  return parts.join(', ')
})
</script>
